import _ from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import { checkCardFromRule } from '../../../component/card/card-from';
import GlobalNotification from '../../../component/common/snackbar';
import i18n from '../../../i18n';
import { GybDevice, GybThirdParty, Restaurant } from '../../../models';
import { BindGybRegisterParam } from '../../../models/fixed/bind-gyb-register';
import apiManage from '../../../request';
import { IRestaurantInfoState } from './restaurant-info-state';
import { ResellerGYBDialogCardConfig } from './restaurant-reseller-gyb';


export class ResellerGYBDialogState {

    constructor() {
        makeAutoObservable(this);
    }

    // *********************
    // Attributes
    // *********************
    // 餐厅信息
    restaurantInfo: Restaurant = {};
    // 弹窗组件类型
    dialogComponentType?: IRestaurantInfoState['componentType'];
    // 餐厅stringId
    restaurantId?: string;
    //
    editGYBData: EditGYBData = {};
    // 编辑GYB源数据
    editGYBSource: EditGYBData = {};
    // 编辑输入框操作map
    @observable operateMap: Map<string, { origin: number, current: number }> = new Map();
    // 第三方经销商
    thirdPartyReseller: GybThirdParty[] = [];
    // GYB列表
    GYBList: GybDevice[] = [];
    // 编辑GYB对象
    GYBDeviceInfo: GybDevice = {};
    // 加载
    isLoading: boolean = false;
    // 空校验
    allowNullVerification: boolean = false;

    // *********************
    // Function
    // *********************

    async init() {
        await Promise.all([this.initGYBData(), this.getThirdPartyReseller(), this.setGYBData()])
    }

    updateIsLoading(val: boolean) {
        this.isLoading = val;
    }

    updateDialogType(val: IRestaurantInfoState['componentType']) {
        this.dialogComponentType = val;
    }

    /**
     * 获取第三方经销商
     * - ADD api获取
     * - EDIT 获取已绑定的
     */
    async getThirdPartyReseller() {
        if (this.dialogComponentType === 'ADD') {
            const result = await apiManage.get_third_party_reseller();
            if (!result?.error) {
                runInAction(() => {
                    this.thirdPartyReseller = result?.[0]?.dbResults || []
                })
            }
        } else {
            runInAction(() => {
                this.thirdPartyReseller = this.GYBDeviceInfo?.belonged_dealer || []
            })
        }
    }

    // 编辑信息
    updateGYBData(val: string, key: string) {
        const cur = val;
        const origin = this.editGYBSource[key as keyof EditGYBData];
        // 判断源数据与新输入数据是否相等
        if (origin !== cur) {
            this.operateMap.set(key, { origin: Number(origin), current: Number(cur) })
        } else this.operateMap.delete(key);
        _.set(this.editGYBData, key, val);
        // this.editGYBData = Object.assign({}, this.editGYBData, val);
    }

    // 清空gyb表单数据
    clearGybFormInfo() {
        // this.editGYBData = {};
        this.operateMap = new Map();
    }

    updateAllowNullVerification(val: boolean) {
        this.allowNullVerification = val;
    }

    updateGYBDeviceInfo(GYBDeviceInfo?: GybDevice) {
        this.GYBDeviceInfo = GYBDeviceInfo || {};
    }
    /**
     * 更新餐厅信息
     */
    updateRestaurantInfo(info: Restaurant) {
        this.restaurantInfo = info;
    }

    updateGYBList(list: GybDevice[]) {
        this.GYBList = list;
    }

    // 初始化数据
    initGYBData() {
        this.editGYBData = {
            reg_vat_number: '',
            reg_fiscal_code: '',
            description: '',
            email: '',
            reg_local: '',
            reg_postcode: '',
            reg_city: '',
            reg_state: '',
            reg_country: '',
            // 基础信息
            // 注册Id
            locationId: '',
            // 设备码
            deviceCode: '',
            // 设备品牌
            brand: '',
            // 设备码
            model: '',
            // 设备码
            licenseCode: '',
            // 中介 默认7
            intermediaryType: '7',
            // 餐厅名称
            name: '',
            // 门店地址
            local: '',
        }
        this.allowNullVerification = false;
        this.operateMap = new Map();
    }

    /**
     * 设置数据
     * - 创建第一个设备，默认填充餐厅信息。
     * - 后续的设备，获取第一个设备的信息，填充餐厅信息
     */
    async setGYBData() {
        const {
            reg_vat_number, reg_fiscal_code, reg_name, email, reg_local, reg_postcode, reg_city, reg_state, reg_country,
        } = this.restaurantInfo;
        const FirstDevice = this.GYBList?.[0];
        let GYBDeviceData: GybDevice = {};
        // 已有设备
        if (FirstDevice) {
            const GYBDeviceRes = await apiManage.get_gyb_restaurant_condition({
                restaurant_string_id: this.restaurantInfo?.string_id || ''
            });
            GYBDeviceData = GYBDeviceRes?.[0].dbResults?.[0] || {};
        }

        runInAction(() => {
            this.editGYBData = {
                reg_vat_number: (FirstDevice ? GYBDeviceData.vat_number : reg_vat_number) || '',
                reg_fiscal_code: (FirstDevice ? GYBDeviceData.tax_code : reg_fiscal_code) || '',
                // 描述 默认是公司名称
                description: (FirstDevice ? GYBDeviceData.description : reg_name) || '',
                email: (FirstDevice ? GYBDeviceData.email : email) || '',
                reg_local: (FirstDevice ? GYBDeviceData.address : reg_local) || '',
                reg_postcode: (FirstDevice ? GYBDeviceData.zip_code : reg_postcode) || '',
                reg_city: (FirstDevice ? GYBDeviceData.city : reg_city) || '',
                reg_state: (FirstDevice ? GYBDeviceData.province : reg_state) || '',
                reg_country: (FirstDevice ? GYBDeviceData.country : reg_country) || '',
                // 基础信息
                // 注册Id
                locationId: FirstDevice?.location_id || '',
                // 设备码
                deviceCode: this.dialogComponentType === 'ADD' ? '' : this.GYBDeviceInfo.device_code,
                // 设备品牌
                brand: this.dialogComponentType === 'ADD' ? '' : this.GYBDeviceInfo.brand,
                // 设备模型
                model: this.dialogComponentType === 'ADD' ? '' : this.GYBDeviceInfo.model,
                // 设备码
                licenseCode: this.dialogComponentType === 'ADD' ? '' : this.GYBDeviceInfo.license_code,
                // 中介
                intermediaryType: GYBDeviceData?.intermediaryType?.toString() ?? '7',
                // 餐厅名称
                name: GYBDeviceData?.name || '',
                // 门店地址
                local: GYBDeviceData?.local || '',
            }
            this.editGYBSource = _.cloneDeep(this.editGYBData);
        })

    }

    // 保存
    async onSave(saveCallback: (error: boolean) => void) {
        // 检查规则
        const errorlist = checkCardFromRule({ config: ResellerGYBDialogCardConfig(), data: this.editGYBData as {} });
        this.updateAllowNullVerification(true)
        if (errorlist?.length) {

            return
        }
        // 必选一个密钥
        if (!this.editGYBData.dealer) {
            GlobalNotification.error(i18n.t('gyb_page_text_please_choose_key'));
            return
        }
        // 判断是否已有设备   GYBList
        const hasGYBDevice = this.GYBList?.length > 0;

        // 追加设备则不需要传税务信息、地址
        let otherParam: BindGybRegisterParam = {};
        if (hasGYBDevice) {
            otherParam = {}
        } else {
            otherParam = {
                // 运营商的增值税注册号
                vatNumber: this.editGYBData.reg_vat_number,
                // 运营商的税号
                taxCode: this.editGYBData.reg_fiscal_code,
                // 描述
                description: this.editGYBData.description,
                // 地址
                address: this.editGYBData?.reg_local,
                // 邮编
                ZipCode: this.editGYBData.reg_postcode,
                // 城市
                city: this.editGYBData.reg_city,
                // 省
                province: this.editGYBData.reg_state,
                // 国家
                country: this.editGYBData.reg_country,
                // Valore Significato 字符串转为数字
                intermediaryType: Number(this.editGYBData.intermediaryType),
                // 邮箱
                email: this.editGYBData.email
            }
        }
        // 选择的密钥
        const param: BindGybRegisterParam = {
            ...otherParam,
            // 运营商的location_id
            locationId: this.editGYBData.locationId || undefined,
            // 设备号，唯一
            deviceCode: this.editGYBData.deviceCode,
            // 品牌
            brand: this.editGYBData.brand,
            // 模型
            model: this.editGYBData.model,
            // GYB 密钥
            licenseCode: this.editGYBData.licenseCode,
            // 密钥
            dealer: this.editGYBData.dealer,
            // 餐厅Id
            restaurant: this.restaurantInfo._id,
            // 餐厅名称
            name: this.editGYBData.name,
            // 门店地址
            local: this.editGYBData.local
        }
        // 加载动画
        // setLoading(true);
        const result = await apiManage.bind_gyb_register(param);

        if (!result?.error) {
            saveCallback?.(false);
            GlobalNotification.success(i18n.t('gyb_page_text_the_addition_of_the_GYB_device_was_successful'));
        } else {
            saveCallback?.(true);
            if (typeof result === 'string') {
                GlobalNotification.error(i18n.t('gyb_page_text_adding_a_GYB_device_failed'));
            } else {
                GlobalNotification.error(`${result?.result?.details}`);
            }
        }
        // setLoading(false);

    }
}

type EditGYBData = {
    reg_vat_number?: string,
    reg_fiscal_code?: string,
    // 描述 默认是公司名称
    description?: string,
    // 餐厅邮箱
    email?: string,
    reg_local?: string,
    reg_postcode?: string,
    reg_city?: string,
    reg_state?: string,
    reg_country?: string,


    // 餐厅ID
    restaurant?: string,

    // 注册Id
    locationId?: string,
    // 设备码
    deviceCode?: string,
    // 设备品牌
    brand?: string,
    // 设备码
    model?: string,
    // 设备码
    licenseCode?: string,
    // 中介
    intermediaryType?: string,
    // 餐厅名称
    name?: string,
    // 门店地址
    local?: string,

    // 密钥
    dealer?: string;
}